/**
 * Map pointer client coordinates to SVG user space.
 *
 * Prefer the element's screen CTM so results match how the SVG is actually
 * drawn (including `preserveAspectRatio` letterboxing). A naive linear map from
 * the layout box to viewBox is wrong when the viewBox aspect ratio differs
 * from the element's box.
 */
export function clientToSvgUserSpace(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number,
    viewBoxAttr: string,
): { x: number; y: number } | null {
    const ctm = svg.getScreenCTM();
    if (ctm) {
        const pt = svg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const p = pt.matrixTransform(ctm.inverse());
        return { x: p.x, y: p.y };
    }

    const viewBoxParts = viewBoxAttr.trim().split(/\s+/).map(Number);
    if (
        viewBoxParts.length !== 4 ||
        viewBoxParts.some((n) => !Number.isFinite(n))
    ) {
        return null;
    }
    const [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] = viewBoxParts;
    if (viewBoxWidth <= 0 || viewBoxHeight <= 0) return null;
    const rect = svg.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    return {
        x: (localX / rect.width) * viewBoxWidth + viewBoxX,
        y: (localY / rect.height) * viewBoxHeight + viewBoxY,
    };
}
